// ============================================================================
// FarmerHomePage — placeholder /#my landing for logged-in farmers (Task #10).
// ============================================================================
// Task #11 fleshes this out with My Registrations + My Attendance. For now
// it confirms the auth round-trip works and gives the farmer a clean signed-in
// experience instead of a 404 after login.
// ============================================================================

import { ClipboardList, FileText, Loader2, LogOut, Mail, PenLine, UserCircle, LayoutGrid } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, type Farmer } from '../api';
import { useFarmerAuth } from '../farmerAuth';

export function FarmerHomePage() {
  const { token, farmer, logout } = useFarmerAuth();
  const [profile, setProfile] = useState<Farmer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      window.location.hash = '#farmer-signin';
      return;
    }
    api.farmerMe(token)
      .then((r) => setProfile(r.farmer))
      .catch((err) => setError(err?.message || 'Could not load profile.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (!token || !farmer) return null;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="border-b border-gray-200 bg-white">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" aria-label="Up in Farms — home">
            <img src="/logo.jpg" alt="Up in Farms Food Hub" className="h-12 md:h-14 w-auto" />
          </a>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-gray-600">
              <UserCircle className="inline h-4 w-4 mr-1 -mt-0.5" />
              {farmer.full_name}
            </span>
            <button
              type="button"
              onClick={() => {
                logout();
                window.location.hash = '#';
              }}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-4 py-8 w-full space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome, {farmer.full_name.split(/\s+/)[0]}</h1>
          <p className="text-sm text-gray-600 mt-1">Your Up in Farms producer account.</p>
        </div>

        {loading && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your profile…
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">{error}</div>
        )}

        {profile && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-2">
            <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
              <Mail className="h-4 w-4" /> Profile
            </h2>
            <div className="text-sm text-gray-700">
              <div><strong>Name:</strong> {profile.full_name}</div>
              <div><strong>Email:</strong> {profile.email}</div>
              {profile.phone && <div><strong>Phone:</strong> {profile.phone}</div>}
              {profile.last_login_at && (
                <div className="text-xs text-gray-500 mt-2">
                  Last sign-in: {new Date(profile.last_login_at).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        )}

        <a
          href="#my/registrations"
          className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-uif-green hover:shadow-sm transition"
        >
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <Mail className="h-4 w-4" /> My registrations
          </h2>
          <p className="text-sm text-gray-700 mt-1">
            See your upcoming workshops, past attendance history, and join links for online events. Cancel registrations you can't make.
          </p>
        </a>

        <a
          href="#my/documents"
          className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-uif-green hover:shadow-sm transition"
        >
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <FileText className="h-4 w-4" /> Documents
          </h2>
          <p className="text-sm text-gray-700 mt-1">
            View and download documents Up in Farms has shared with your farm — agreements, maps, reports, and program paperwork.
          </p>
        </a>

        <a
          href="#my/signatures"
          className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-uif-green hover:shadow-sm transition"
        >
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <PenLine className="h-4 w-4" /> Signatures
          </h2>
          <p className="text-sm text-gray-700 mt-1">
            Review and electronically sign documents Up in Farms has sent you — agreements and program paperwork awaiting your signature.
          </p>
        </a>

        <a
          href="#my/enrollment"
          className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-uif-green hover:shadow-sm transition"
        >
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <ClipboardList className="h-4 w-4" /> Enrollment
          </h2>
          <p className="text-sm text-gray-700 mt-1">
            See your AMP program enrollment status and what&apos;s left on your eligibility checklist.
          </p>
        </a>

        <a
          href="#my/tracks"
          className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-uif-green hover:shadow-sm transition"
        >
          <h2 className="text-base font-semibold text-uif-green-dark flex items-center gap-2">
            <LayoutGrid className="h-4 w-4" /> Track plans
          </h2>
          <p className="text-sm text-gray-700 mt-1">
            Track your conservation, production, food safety, and marketing milestones and see what evidence is needed next.
          </p>
        </a>

        <a
          href="/"
          className="block bg-white border border-gray-200 rounded-lg p-5 hover:border-uif-green hover:shadow-sm transition"
        >
          <h2 className="text-base font-semibold text-uif-green-dark">Browse upcoming workshops</h2>
          <p className="text-sm text-gray-700 mt-1">
            See what's coming up on the public training calendar.
          </p>
        </a>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6">
        <div className="max-w-3xl mx-auto px-4 text-center text-xs text-gray-500">
          Up in Farms LLC · <a href="mailto:support@upinfarms.com" className="text-uif-green hover:underline">support@upinfarms.com</a>
        </div>
      </footer>
    </div>
  );
}
